import { clsx } from 'clsx';
import {
  type ReactNode,
  type ComponentPropsWithoutRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import { EVENTKEY_MAP } from '@repo/lib/constants';
import { debounce } from '@repo/lib/functions';
import { isPopoverOpen } from '@repo/dom/dom';
import type { Alignment, Placement, Offset } from '@repo/dom/position';

import { applyFloatingPosition } from '@repo/ui/FloatingContent/applyFloatingPosition';
import { FloatingPositionProvider } from '@repo/ui/FloatingContent';
import { useTooltip } from './TooltipRoot.js';

import s from './Tooltip.module.css';

export type TooltipProps = ComponentPropsWithoutRef<'div'> & {
  align?: Alignment;
  children?: ReactNode;
  className?: string;
  hasArrow?: boolean;
  offset?: Offset;
  placement?: Placement;
  viewportMargin?: number;
};

export const Tooltip = (props: TooltipProps) => {
  const {
    align = 'center',
    children,
    className,
    hasArrow = false,
    offset = 4,
    placement = 'top',
    viewportMargin = 4,
    ...attrs
  } = props;
  const { close, id, isOpen, tooltipRef, triggerRef } = useTooltip();
  const arrowRef = useRef<HTMLSpanElement>(null);

  const handleResize = useCallback(() => {
    close();
  }, [close]);

  const debouncedHandleResize = useMemo(
    () => debounce(handleResize, 150),
    [handleResize],
  );

  const calculateAndApplyPosition = useCallback(() => {
    if (!tooltipRef.current || !triggerRef.current) return;

    applyFloatingPosition({
      floating: tooltipRef.current,
      trigger: triggerRef.current,
      arrow: arrowRef.current,
      placement,
      align,
      offset,
      viewportMargin,
      hasArrow,
    });
  }, [
    tooltipRef,
    triggerRef,
    placement,
    align,
    offset,
    viewportMargin,
    hasArrow,
  ]);

  useLayoutEffect(() => {
    const tooltip = tooltipRef.current;
    if (!tooltip) return;

    if (isOpen) {
      if (!isPopoverOpen(tooltip)) {
        tooltip.showPopover?.();
      }
      calculateAndApplyPosition();
    } else if (isPopoverOpen(tooltip)) {
      tooltip.hidePopover?.();
    }
  }, [isOpen, tooltipRef, calculateAndApplyPosition]);

  // Gated on `isOpen` because a closed tooltip has nothing to reposition, and
  // a page can hold a lot of them — one per row of a table, say — each of
  // which would otherwise hold a window listener and a debounce timer for a
  // popover nobody has opened.
  useEffect(() => {
    if (!isOpen) return;

    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
      debouncedHandleResize.cancel();
    };
  }, [isOpen, debouncedHandleResize]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === EVENTKEY_MAP.ESCAPE && !e.defaultPrevented) {
        e.preventDefault();
        close();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  const floatingPositionValue = useMemo(
    () => ({ arrowRef, hasArrow }),
    [hasArrow],
  );

  return (
    <div
      className={s.tooltipEl}
      data-has-arrow={hasArrow}
      id={id}
      popover="manual"
      role="tooltip"
      ref={tooltipRef}
      {...attrs}
    >
      <div
        style={{ visibility: isOpen ? 'visible' : 'hidden' }}
        className={clsx(s.tooltip, className)}
      >
        <FloatingPositionProvider value={floatingPositionValue}>
          {children}
        </FloatingPositionProvider>
      </div>
    </div>
  );
};
